export interface HSL {
  h: number;
  s: number;
  l: number;
}

export function hexToHSL(hex: string): HSL {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, '');

  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  
  // Length alone is not a hex check: parseInt() returns NaN for non-hex digits
  // ('zzzzzz' is six characters long), which would emit `--primary: NaN NaN% NaN%`.
  if (!/^[0-9a-f]{6}$/i.test(hex)) return { h: 0, s: 0, l: 0 }; // Fallback

  // Parse r, g, b values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

export function generateThemeVariables(
  baseColorHex: string, 
  radius: number, 
  isDark: boolean = false,
  secondaryColorHex?: string,
  backgroundColorHex?: string,
  textColorHex?: string,
  borderColorHex?: string,
  borderWidth?: number
) {
  const baseHSL = hexToHSL(baseColorHex);
  const secHSL = secondaryColorHex ? hexToHSL(secondaryColorHex) : { ...baseHSL, s: Math.max(10, baseHSL.s - 20) };
  const bgHSL = backgroundColorHex ? hexToHSL(backgroundColorHex) : baseHSL;
  const textHSL = textColorHex ? hexToHSL(textColorHex) : null;
  const borderHSL = borderColorHex ? hexToHSL(borderColorHex) : null;
  
  // Tailwind uses format: H S% L%
  const toTailwindFormat = (h: number, s: number, l: number) => `${h} ${s}% ${l}%`;
  
  if (!isDark) {
    return {
      '--background': backgroundColorHex ? toTailwindFormat(bgHSL.h, bgHSL.s, 98) : toTailwindFormat(bgHSL.h, 20, 98),
      '--foreground': textHSL ? toTailwindFormat(textHSL.h, textHSL.s, textHSL.l) : toTailwindFormat(bgHSL.h, 40, 10),
      '--card': backgroundColorHex ? toTailwindFormat(bgHSL.h, bgHSL.s, 100) : '0 0% 100%',
      '--card-foreground': textHSL ? toTailwindFormat(textHSL.h, textHSL.s, textHSL.l) : toTailwindFormat(bgHSL.h, 40, 10),
      '--popover': backgroundColorHex ? toTailwindFormat(bgHSL.h, bgHSL.s, 100) : '0 0% 100%',
      '--popover-foreground': textHSL ? toTailwindFormat(textHSL.h, textHSL.s, textHSL.l) : toTailwindFormat(bgHSL.h, 40, 10),
      '--primary': toTailwindFormat(baseHSL.h, baseHSL.s, baseHSL.l),
      '--primary-foreground': baseHSL.l > 60 ? '0 0% 10%' : '0 0% 100%',
      '--secondary': toTailwindFormat(secHSL.h, secHSL.s, 90),
      '--secondary-foreground': toTailwindFormat(secHSL.h, Math.max(secHSL.s, 40), 10),
      '--btn-surface': backgroundColorHex ? toTailwindFormat(bgHSL.h, bgHSL.s, 98) : toTailwindFormat(bgHSL.h, 20, 98),
      '--btn-surface-text': textHSL ? toTailwindFormat(textHSL.h, textHSL.s, textHSL.l) : toTailwindFormat(bgHSL.h, 40, 10),
      '--btn-surface-border': borderHSL ? toTailwindFormat(borderHSL.h, borderHSL.s, borderHSL.l) : (backgroundColorHex ? toTailwindFormat(bgHSL.h, bgHSL.s, 85) : toTailwindFormat(bgHSL.h, 20, 85)),
      '--muted': backgroundColorHex ? toTailwindFormat(bgHSL.h, bgHSL.s, 92) : toTailwindFormat(bgHSL.h, 10, 92),
      '--muted-foreground': textHSL ? toTailwindFormat(textHSL.h, Math.max(textHSL.s, 10), Math.min(textHSL.l + 30, 60)) : (backgroundColorHex ? toTailwindFormat(bgHSL.h, Math.max(bgHSL.s, 10), 45) : toTailwindFormat(bgHSL.h, 10, 45)),
      '--accent': toTailwindFormat(secHSL.h, secHSL.s, 90),
      '--accent-foreground': toTailwindFormat(secHSL.h, secHSL.s, 30),
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': borderHSL ? toTailwindFormat(borderHSL.h, borderHSL.s, borderHSL.l) : (backgroundColorHex ? toTailwindFormat(bgHSL.h, bgHSL.s, 85) : toTailwindFormat(bgHSL.h, 20, 85)),
      '--input': borderHSL ? toTailwindFormat(borderHSL.h, borderHSL.s, borderHSL.l) : (backgroundColorHex ? toTailwindFormat(bgHSL.h, bgHSL.s, 85) : toTailwindFormat(bgHSL.h, 20, 85)),
      '--ring': toTailwindFormat(baseHSL.h, baseHSL.s, baseHSL.l),
      '--radius': `${radius}rem`,
      // Only emitted when a width was given: an `undefined` value was written
      // out as the literal `--custom-border-width: undefined;`, so the CSS
      // `var(--custom-border-width, 1px)` fallback never applied.
      ...(borderWidth !== undefined ? { '--custom-border-width': `${borderWidth}px` } : {}),
    };
  } else {
    // Dark mode
    return {
      '--background': backgroundColorHex ? toTailwindFormat(bgHSL.h, Math.min(bgHSL.s, 40), 8) : toTailwindFormat(bgHSL.h, 30, 8),
      '--foreground': textHSL ? toTailwindFormat(textHSL.h, textHSL.s, textHSL.l) : toTailwindFormat(bgHSL.h, 20, 90),
      '--card': backgroundColorHex ? toTailwindFormat(bgHSL.h, Math.min(bgHSL.s, 40), 12) : toTailwindFormat(bgHSL.h, 30, 12),
      '--card-foreground': textHSL ? toTailwindFormat(textHSL.h, textHSL.s, textHSL.l) : toTailwindFormat(bgHSL.h, 20, 90),
      '--popover': backgroundColorHex ? toTailwindFormat(bgHSL.h, Math.min(bgHSL.s, 40), 12) : toTailwindFormat(bgHSL.h, 30, 12),
      '--popover-foreground': textHSL ? toTailwindFormat(textHSL.h, textHSL.s, textHSL.l) : toTailwindFormat(bgHSL.h, 20, 90),
      '--primary': toTailwindFormat(baseHSL.h, baseHSL.s, baseHSL.l),
      '--primary-foreground': baseHSL.l > 60 ? '0 0% 10%' : '0 0% 100%',
      '--secondary': toTailwindFormat(secHSL.h, secHSL.s, 20),
      '--secondary-foreground': toTailwindFormat(secHSL.h, 20, 90),
      '--btn-surface': backgroundColorHex ? toTailwindFormat(bgHSL.h, Math.min(bgHSL.s, 40), 8) : toTailwindFormat(bgHSL.h, 30, 8),
      '--btn-surface-text': textHSL ? toTailwindFormat(textHSL.h, textHSL.s, textHSL.l) : toTailwindFormat(bgHSL.h, 20, 90),
      '--btn-surface-border': borderHSL ? toTailwindFormat(borderHSL.h, borderHSL.s, borderHSL.l) : (backgroundColorHex ? toTailwindFormat(bgHSL.h, Math.min(bgHSL.s, 40), 25) : toTailwindFormat(bgHSL.h, 20, 25)),
      '--muted': backgroundColorHex ? toTailwindFormat(bgHSL.h, Math.min(bgHSL.s, 20), 20) : toTailwindFormat(bgHSL.h, 10, 20),
      '--muted-foreground': textHSL ? toTailwindFormat(textHSL.h, Math.min(textHSL.s, 20), Math.max(textHSL.l - 30, 40)) : (backgroundColorHex ? toTailwindFormat(bgHSL.h, Math.min(bgHSL.s, 20), 60) : toTailwindFormat(bgHSL.h, 10, 60)),
      '--accent': toTailwindFormat(secHSL.h, secHSL.s, 25),
      '--accent-foreground': toTailwindFormat(secHSL.h, secHSL.s, 85),
      '--destructive': '0 62% 30%',
      '--destructive-foreground': '0 0% 100%',
      '--border': borderHSL ? toTailwindFormat(borderHSL.h, borderHSL.s, borderHSL.l) : (backgroundColorHex ? toTailwindFormat(bgHSL.h, Math.min(bgHSL.s, 40), 25) : toTailwindFormat(bgHSL.h, 20, 25)),
      '--input': borderHSL ? toTailwindFormat(borderHSL.h, borderHSL.s, borderHSL.l) : (backgroundColorHex ? toTailwindFormat(bgHSL.h, Math.min(bgHSL.s, 40), 25) : toTailwindFormat(bgHSL.h, 20, 25)),
      '--ring': toTailwindFormat(baseHSL.h, baseHSL.s, baseHSL.l),
      '--radius': `${radius}rem`,
      ...(borderWidth !== undefined ? { '--custom-border-width': `${borderWidth}px` } : {}),
    };
  }
}
